/**
 * Tab Error Boundary
 *
 * Catches errors within individual tabs and displays a friendly error UI
 * without crashing the entire application. Other tabs remain functional.
 *
 * NOTE: Chunk load errors (from dynamic imports after deployments) are NOT
 * caught here - they are re-thrown to be handled by the root ChunkErrorBoundary
 * which has auto-refresh logic and handles unsaved form state properly.
 */

import React, { Component, type ReactNode } from 'react';
import { Result, Button, Typography, Space, Collapse, message } from 'antd';
import {
  ReloadOutlined,
  CloseOutlined,
  BugOutlined,
  CopyOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { ChunkErrorBoundary } from '../ChunkErrorBoundary';

const { Text, Paragraph } = Typography;

/**
 * Error context that gets passed to the Report Issue form.
 * Used by TabErrorBoundary (React errors) and HTTP/tRPC error handlers.
 */
export interface ErrorReportContext {
  errorMessage: string;
  errorName: string;
  errorStack?: string;
  componentStack?: string;
  url: string;
  path: string;
  tabKey?: string;
  timestamp: string;
  userAgent: string;
  httpStatus?: number;
  requestUrl?: string;
  requestMethod?: string;
  errorCode?: string;
  requestBody?: string;
  responseBody?: string;
}

/**
 * Storage key for passing error context to the report form
 */
export const ERROR_REPORT_CONTEXT_KEY = 'utx_error_report_context';

/**
 * Format error context into a markdown description
 */
export function formatErrorReportDescription(context: ErrorReportContext): string {
  const httpSection = context.httpStatus
    ? `\n**HTTP Status:** ${context.httpStatus}\n**Request:** ${context.requestMethod ?? 'unknown'} ${context.requestUrl ?? 'unknown'}${context.errorCode ? `\n**Error Code:** ${context.errorCode}` : ''}`
    : '';

  const stackSection = context.errorStack
    ? `### Stack Trace\n\`\`\`\n${context.errorStack}\n\`\`\``
    : '';

  const componentSection = context.componentStack
    ? `### Component Stack\n\`\`\`\n${context.componentStack}\n\`\`\``
    : '';

  return `## Error Report

**Page:** ${context.path}
**Time:** ${context.timestamp}
**Browser:** ${context.userAgent}${httpSection}

### Error Message
\`\`\`
${context.errorName}: ${context.errorMessage}
\`\`\`

${[stackSection, componentSection].filter(Boolean).join('\n\n')}

---

### What I was doing when this happened
[Please describe what you were doing when this error occurred]

### Steps to reproduce
1. 
2. 
3. 
`;
}

interface TabErrorBoundaryProps {
  children: ReactNode;
  tabKey: string;
  path: string;
  onClose?: () => void;
  onRetry?: () => void;
}

interface TabErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  isChunkError: boolean;
}

/**
 * Error boundary that wraps individual tab content.
 * When an error occurs, it shows a friendly error UI within the tab
 * without affecting other tabs.
 */
export class TabErrorBoundary extends Component<TabErrorBoundaryProps, TabErrorBoundaryState> {
  constructor(props: TabErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isChunkError: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<TabErrorBoundaryState> {
    // Check if this is a chunk load error - if so, we'll re-throw it in render
    // to let ChunkErrorBoundary handle it with proper auto-refresh logic
    const isChunkError = ChunkErrorBoundary.isChunkLoadError(error);
    
    return {
      hasError: true,
      error,
      isChunkError,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log the error for debugging/monitoring
    console.error('[TabErrorBoundary] Error in tab:', {
      tabKey: this.props.tabKey,
      path: this.props.path,
      message: error.message,
      name: error.name,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });

    this.setState({ errorInfo });
  }

  /**
   * Build error context for reporting
   */
  private buildErrorContext(): ErrorReportContext {
    const { error, errorInfo } = this.state;
    const { path, tabKey } = this.props;

    return {
      errorMessage: error?.message || 'Unknown error',
      errorName: error?.name || 'Error',
      errorStack: error?.stack,
      componentStack: errorInfo?.componentStack || undefined,
      url: window.location.href,
      path,
      tabKey,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
    };
  }

  /**
   * Navigate to report issue form with error context
   */
  private handleReportIssue = (): void => {
    const errorContext = this.buildErrorContext();
    
    // Store error context in sessionStorage for the form to pick up
    sessionStorage.setItem(ERROR_REPORT_CONTEXT_KEY, JSON.stringify(errorContext));
    
    // Navigate to the report issue page
    window.location.href = '/support-center/report-issue';
  };

  /**
   * Reset error state and retry rendering the component
   */
  private handleRetry = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      isChunkError: false,
    });

    // Call parent retry handler if provided
    this.props.onRetry?.();
  };

  /**
   * Copy error details to clipboard for bug reports
   */
  private handleCopyError = (): void => {
    const errorContext = this.buildErrorContext();
    const errorReport = formatErrorReportDescription(errorContext);

    navigator.clipboard.writeText(errorReport).then(() => {
      message.success('Error details copied to clipboard');
    }).catch(() => {
      message.error('Failed to copy error details');
    });
  };

  render(): React.ReactNode {
    const { hasError, error, isChunkError } = this.state;
    const { children, path, onClose } = this.props;

    if (!hasError) {
      return children;
    }

    // Re-throw chunk load errors to let ChunkErrorBoundary handle them
    // ChunkErrorBoundary has proper auto-refresh logic and handles unsaved form state
    if (isChunkError && error) {
      throw error;
    }

    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        padding: '40px 20px',
        background: '#fafafa',
      }}>
        <Result
          status="error"
          title="This tab encountered an error"
          subTitle={
            <Space direction="vertical" size="small" style={{ textAlign: 'center' }}>
              <Text>The page at <Text code>{path}</Text> failed to render.</Text>
              <Text type="secondary">Other tabs are unaffected and continue to work normally.</Text>
            </Space>
          }
          extra={
            <Space wrap style={{ justifyContent: 'center' }}>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={this.handleRetry}
              >
                Try Again
              </Button>
              <Button
                type="default"
                icon={<ExclamationCircleOutlined />}
                onClick={this.handleReportIssue}
                style={{
                  borderColor: '#8b5cf6',
                  color: '#8b5cf6',
                }}
              >
                Report Issue
              </Button>
              {onClose && (
                <Button
                  icon={<CloseOutlined />}
                  onClick={onClose}
                >
                  Close Tab
                </Button>
              )}
              <Button
                icon={<CopyOutlined />}
                onClick={this.handleCopyError}
              >
                Copy Error
              </Button>
            </Space>
          }
        />

        {/* Collapsible error details for debugging */}
        <div style={{ width: '100%', maxWidth: 600, marginTop: 24 }}>
          <Collapse
            ghost
            items={[
              {
                key: 'details',
                label: (
                  <Space>
                    <BugOutlined />
                    <Text type="secondary">Error Details</Text>
                  </Space>
                ),
                children: (
                  <div style={{
                    background: '#1e1e1e',
                    borderRadius: 8,
                    padding: 16,
                    maxHeight: 300,
                    overflow: 'auto',
                  }}>
                    <Paragraph
                      style={{
                        fontFamily: 'monospace',
                        fontSize: 12,
                        color: '#f8f8f2',
                        margin: 0,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      <Text style={{ color: '#ff5555' }}>{error?.name}: </Text>
                      <Text style={{ color: '#f1fa8c' }}>{error?.message}</Text>
                      {'\n\n'}
                      <Text style={{ color: '#6272a4' }}>// Stack trace</Text>
                      {'\n'}
                      <Text style={{ color: '#bd93f9' }}>
                        {error?.stack?.split('\n').slice(1).join('\n')}
                      </Text>
                    </Paragraph>
                  </div>
                ),
              },
            ]}
          />
        </div>

      </div>
    );
  }
}

export default TabErrorBoundary;
